'use client'

import React from 'react'

import { HiPlus, HiX } from 'react-icons/hi'

import { Box, Flex, Icon, Text } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'
import { Button } from '../Button'
import { CardCollapsibleRow, CardExpandable } from '../Card'
import { Chip } from '../Chip'
import { MultilineTextInput } from '../MultilineTextInput'
import { Select, type SelectOption, type SelectPrimaryAction } from '../Select'
import { TextInput } from '../TextInput'
import { containerTokens } from './WorkflowStep.styles'
import type { WorkflowStep as WorkflowStepData } from './types'

export interface WorkflowStepIProps {
  step: WorkflowStepData
  stepNumber: number
  onToggleExpand: (id: string) => void
  onInstructionChange: (id: string, value: string) => void
  onOutcomeChange: (id: string, index: number, value: string) => void
  onAddOutcome: (id: string) => void
  onRemoveOutcome: (id: string, index: number) => void
  onDuplicate: (id: string) => void
  onDelete: (id: string) => void
  availableNextSteps: SelectOption[]
  onOutcomeNextStepChange: (
    stepId: string,
    outcomeIndex: number,
    nextStepId: string
  ) => void
  onAddNextStep: (stepId: string, outcomeIndex: number) => void
  isReadOnly?: boolean
  colorScheme?: ColorScheme
}

const MAX_INSTRUCTION_LENGTH = 200

export const WorkflowStep = ({
  step,
  stepNumber,
  onToggleExpand,
  onInstructionChange,
  onOutcomeChange,
  onAddOutcome,
  onRemoveOutcome,
  onDuplicate,
  onDelete,
  availableNextSteps,
  onOutcomeNextStepChange,
  onAddNextStep,
  isReadOnly = false,
  colorScheme = 'dark',
}: WorkflowStepIProps) => {
  const t = containerTokens[colorScheme]

  return (
    <Flex alignItems='flex-start' direction='row' gap='16px'>
      <Box flexShrink={0} mt='16px'>
        <Chip
          colorScheme={colorScheme}
          label={`Step ${stepNumber}`}
          variant='category'
        />
      </Box>

      <Box
        bg={t.bg}
        border='1px solid'
        borderColor={t.border}
        flex={1}
        minW={0}
      >
        <CardCollapsibleRow
          alignItems='center'
          colorScheme={colorScheme}
          isExpanded={step.isExpanded}
          mb='0'
          onToggle={() => onToggleExpand(step.id)}
          px='20px'
          py='16px'
        >
          <Text
            color={step.instruction ? t.text : t.textSecondary}
            fontSize='14px'
            mr='16px'
            overflow='hidden'
            textOverflow='ellipsis'
            whiteSpace='nowrap'
          >
            {step.instruction || 'Add an instruction for this step...'}
          </Text>
        </CardCollapsibleRow>

        <CardExpandable isExpanded={step.isExpanded}>
          <Box
            borderTopColor={t.border}
            borderTopWidth='1px'
            px='20px'
            py='16px'
          >
            <Text
              color={t.textSecondary}
              mb='8px'
              textStyle='overline'
              textTransform='uppercase'
            >
              Instruction
            </Text>

            <MultilineTextInput
              colorScheme={colorScheme}
              isDisabled={isReadOnly}
              maxLength={MAX_INSTRUCTION_LENGTH}
              minH='100px'
              onChange={(e) => onInstructionChange(step.id, e.target.value)}
              placeholder='Add an instruction for this step...'
              value={step.instruction}
            />

            <Text
              color={t.textSecondary}
              fontSize='12px'
              mt='6px'
              textAlign='right'
            >
              {step.instruction.length} / {MAX_INSTRUCTION_LENGTH}
            </Text>

            <Text
              color={t.textSecondary}
              mb='8px'
              mt='16px'
              textStyle='overline'
              textTransform='uppercase'
            >
              Outcomes
            </Text>

            <Flex direction='column' gap='8px'>
              {step.outcomes.map((outcome, index) => {
                const primaryAction: SelectPrimaryAction | undefined =
                  isReadOnly
                    ? undefined
                    : {
                        label: '+ Add a new step',
                        onClick: () => onAddNextStep(step.id, index),
                      }

                const selectedNextStep = outcome.nextStepId
                  ? availableNextSteps.find(
                      (s) => s.value === outcome.nextStepId
                    ) ?? null
                  : null

                return (
                  <Flex alignItems='center' gap='8px' key={outcome.id}>
                    <TextInput
                      colorScheme={colorScheme}
                      disabled={isReadOnly}
                      onChange={(e) =>
                        onOutcomeChange(step.id, index, e.target.value)
                      }
                      placeholder='Add an outcome for this step...'
                      value={outcome.value}
                    />
                    <Box w='200px'>
                      <Select
                        colorScheme={colorScheme}
                        isDisabled={isReadOnly}
                        onChange={(opt) => {
                          if (opt) {
                            onOutcomeNextStepChange(step.id, index, opt.value)
                          }
                        }}
                        options={availableNextSteps}
                        placeholder='Select next step...'
                        primaryAction={primaryAction}
                        value={selectedNextStep}
                      />
                    </Box>
                    {index > 0 && !isReadOnly && (
                      <Icon
                        aria-label='Remove outcome'
                        as={HiX}
                        color={t.textSecondary}
                        cursor='pointer'
                        flexShrink={0}
                        h='14px'
                        onClick={() => onRemoveOutcome(step.id, index)}
                        onKeyDown={(e) => {
                          if (
                            (e.key === 'Enter' || e.key === ' ') &&
                            !e.repeat
                          ) {
                            e.preventDefault()
                            onRemoveOutcome(step.id, index)
                          }
                        }}
                        role='button'
                        tabIndex={0}
                        w='14px'
                      />
                    )}
                  </Flex>
                )
              })}
            </Flex>

            {!isReadOnly && (
              <Flex
                alignItems='center'
                border='1px dashed'
                borderColor={t.buttonBorder}
                cursor='pointer'
                gap='8px'
                justifyContent='flex-start'
                mt='8px'
                onClick={() => onAddOutcome(step.id)}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ' ') && !e.repeat) {
                    e.preventDefault()
                    onAddOutcome(step.id)
                  }
                }}
                px='12px'
                py='10px'
                role='button'
                tabIndex={0}
              >
                <Icon as={HiPlus} color={t.textSecondary} h='14px' w='14px' />
                <Text color={t.textSecondary} fontSize='13px' fontWeight='500'>
                  Add outcome
                </Text>
              </Flex>
            )}

            {!isReadOnly && (
              <Flex gap='8px' mt='16px'>
                <Button
                  colorScheme={colorScheme}
                  onClick={() => onDuplicate(step.id)}
                  size='sm'
                  variant='tertiary'
                >
                  Duplicate
                </Button>
                <Button
                  colorScheme={colorScheme}
                  onClick={() => onDelete(step.id)}
                  size='sm'
                  variant='tertiary'
                >
                  Delete step
                </Button>
              </Flex>
            )}
          </Box>
        </CardExpandable>
      </Box>
    </Flex>
  )
}
